import React, { FC, useCallback } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import styled from 'styled-components';
import Typography from '../../../../Typography/Typography';
import { Button } from '../../../../Button/Button';
import { QueryModel, SortModel } from '../../../utils/DataTableQueryProvider/DataTableQuery.types';
import { useDataTableQuickReportsContext } from '../../../utils/DataTableQuickReportsContext';
import { useDataTableQuickActionsContext } from '../../../utils/DataTableQuickActionsContext';
import { filterQuickReports, reorderQuickReports } from '../utils/QuickReports.utils';
import { QuickReportActions } from './QuickReportActions';
import { useDataTableContext } from '../../../utils/DataTableContext';
import { useIntlUds } from '../../../../../translations/UdsIntlProvider';

interface Props {
  goToFilterPanel: () => void;
  setNewQuickReportId: (quickReportId: string) => void;
  isUnSavedState: boolean;
}

export const DnDQuickReports = <Q extends QueryModel, S extends SortModel>({
  setNewQuickReportId,
  goToFilterPanel,
  isUnSavedState
}: Props) => {
  const { intl } = useIntlUds();

  const { quickReports } = useDataTableQuickReportsContext<Q, S>();
  const { isFetching } = useDataTableContext();
  const {
    pinAllQuickReports,
    unPinAllQuickReports,
    selectQuickReport,
    pinQuickReport,
    unPinQuickReport,
    reOrderQuickReports
  } = useDataTableQuickActionsContext();

  const editFilters = useCallback(
    (quickReportId: string) => {
      selectQuickReport(quickReportId);
      setTimeout(() => {
        // Wait for QuickReport was set and loading finished to go to Filter panel
        setNewQuickReportId(quickReportId);
      }, 0);
    },
    [setNewQuickReportId, selectQuickReport]
  );

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const { destination, source, draggableId } = result;
    const isMoveToPinned = destination.droppableId === 'droppable-pinned-quick-reports';
    const isMovingFilters = destination.droppableId !== source.droppableId;
    // Element was moved between unpinned to pinned section or reverse direction.
    if (isMovingFilters && isMoveToPinned) {
      pinQuickReport(draggableId);
    } else if (isMovingFilters && !isMoveToPinned) {
      unPinQuickReport(draggableId);
    }

    const startIndex = (quickReports ?? []).findIndex((filter) => filter.quickReportId === draggableId);
    const endIndex = isMoveToPinned
      ? destination.index
      : // pinned and unpinned quick reports use the same array.
        // So it is needed to include pinned index amount for correct calculation
        destination.index + (pinnedQuickReports.length <= 1 ? 0 : pinnedQuickReports.length - 1);

    const reorderedQuickReports = reorderQuickReports(quickReports ?? [], startIndex, endIndex);
    reOrderQuickReports(reorderedQuickReports);
  };
  const pinnedQuickReports = filterQuickReports(true, quickReports);
  const unPinnedQuickReports = filterQuickReports(false, quickReports);

  const disableAllActions = isFetching || isUnSavedState;

  if (!pinnedQuickReports.length && !unPinnedQuickReports.length) return null;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StyledHeader>
        <Typography variant="body1">{intl.DataTable.QuickViews.Drawer.pinnedQuickViewsLabel}</Typography>
        <Button
          dataTest="quick-reports-unpin-all"
          onClick={unPinAllQuickReports}
          disabled={disableAllActions}
          color="primary"
        >
          {intl.DataTable.QuickViews.Drawer.unpinQuickViewsBtn}
        </Button>
      </StyledHeader>
      <Droppable droppableId="droppable-pinned-quick-reports" isDropDisabled={disableAllActions}>
        {(droppableProvided, droppableSnapshot) => (
          <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
            {pinnedQuickReports.map((filter, index) => (
              <Draggable
                key={filter.quickReportId}
                draggableId={filter.quickReportId}
                index={index}
                isDragDisabled={disableAllActions}
              >
                {(draggableProvided, draggableSnapshot) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                    style={draggableProvided.draggableProps.style}
                  >
                    <QuickReportActions
                      editReport={editFilters}
                      goToFilterPanel={goToFilterPanel}
                      report={filter}
                      isDragging={draggableSnapshot.isDragging}
                      isUnSavedState={isUnSavedState}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {pinnedQuickReports.length === 0 && (
              <NoFiltersSection pinned isDraggingOver={droppableSnapshot.isDraggingOver} />
            )}

            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
      <StyledHeader>
        <Typography variant="body1">{intl.DataTable.QuickViews.Drawer.unpinnedQuickViewsLabel}</Typography>
        <Button
          dataTest="quick-reports-pin-all"
          onClick={pinAllQuickReports}
          disabled={disableAllActions}
          color="primary"
        >
          {intl.DataTable.QuickViews.Drawer.pinQuickViewsBtn}
        </Button>
      </StyledHeader>
      <Droppable droppableId="droppable-unpinned-quick-reports" isDropDisabled={disableAllActions}>
        {(droppableProvided, droppableSnapshot) => (
          <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
            {unPinnedQuickReports.map((filter, index) => (
              <Draggable
                key={filter.quickReportId}
                draggableId={filter.quickReportId}
                index={index}
                isDragDisabled={disableAllActions}
              >
                {(draggableProvided, draggableSnapshot) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                    style={draggableProvided.draggableProps.style}
                  >
                    <QuickReportActions
                      editReport={editFilters}
                      goToFilterPanel={goToFilterPanel}
                      report={filter}
                      isDragging={draggableSnapshot.isDragging}
                      isUnSavedState={isUnSavedState}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {unPinnedQuickReports.length === 0 && (
              <NoFiltersSection pinned={false} isDraggingOver={droppableSnapshot.isDraggingOver} />
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const NoFiltersSection: FC<React.PropsWithChildren<{ pinned: boolean; isDraggingOver: boolean }>> = ({
  pinned,
  isDraggingOver
}) => (
  <StyledSection $isDraggingOver={isDraggingOver} data-test={`empty-section-${pinned ? 'pinned' : 'unpinned'}`}>
    <Typography variant="label1">
      {pinned ? 'Pinned Quick View will appear here' : 'Unpinned Quick View will appear here'}
    </Typography>
    <Typography variant="labelSmall">
      {pinned ? 'Drag and drop or use overflow menu to pin' : 'Drag and drop or use overflow menu to unpin'}
    </Typography>
  </StyledSection>
);

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacingSize.xl};
`;

const StyledSection = styled.div<{ $isDraggingOver: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  height: 120px;
  flex-direction: column;
  opacity: ${({ $isDraggingOver }) => ($isDraggingOver ? 0.3 : 1)};
  margin: ${({ theme }) => theme.spacingSize.m} 0;
  width: 100%;
  gap: ${({ theme }) => theme.spacingSize.m};
  padding: ${({ theme }) => theme.spacingSize.xxl};
  background: ${({ theme }) => theme.paletteNeutralVariant95};
  border: ${({ theme }) => `1px solid ${theme.colorSurfaceVariant}`};
  border-radius: ${({ theme }) => theme.radiusDefault};
  box-sizing: border-box;
`;
